import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2, FileUp, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { uploadImage, uploadFile, fileUrl, formatErr } from "@/lib/api";

export const PdfUpload = ({ value, filename, onChange, testId }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.type !== "application/pdf") return toast.error("Seleziona un file PDF");
    if (f.size > 15 * 1024 * 1024) return toast.error("PDF troppo grande (max 15 MB)");
    setBusy(true);
    try {
      const d = await uploadFile(f);
      onChange(d.url, d.filename);
      toast.success("PDF caricato");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Label className="text-xs text-zinc-600">File PDF (max 15 MB)</Label>
      <button type="button" onClick={() => ref.current.click()} data-testid={testId}
        className="mt-1.5 flex w-full items-center gap-3 rounded-lg border border-dashed bg-white px-3 py-3 text-left text-sm text-zinc-600 transition-colors hover:border-zinc-500">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : value ? <FileText className="h-5 w-5 text-terra" /> : <FileUp className="h-5 w-5" strokeWidth={1.5} />}
        <span className="min-w-0 flex-1 truncate">{value ? filename || "Documento caricato" : "Carica un PDF"}</span>
        {value && <span className="text-xs text-zinc-400">Sostituisci</span>}
      </button>
      {value && <a href={fileUrl(value)} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-[11px] text-terra" data-testid={`${testId}-open`}>Apri anteprima</a>}
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={pick} />
    </div>
  );
};

export const Field = ({ label, value, onChange, testId, placeholder, type = "text", area, rows = 3, hint, className = "" }) => (
  <div className={className}>
    <Label className="text-xs text-zinc-600">{label}</Label>
    {area ? (
      <Textarea data-testid={testId} value={value || ""} placeholder={placeholder} rows={rows} onChange={(e) => onChange(e.target.value)} className="mt-1.5 bg-white" />
    ) : (
      <Input data-testid={testId} type={type} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1.5 bg-white" />
    )}
    {hint && <p className="mt-1 text-[11px] text-zinc-400">{hint}</p>}
  </div>
);

export const SwitchRow = ({ label, checked, onChange, testId, hint }) => (
  <label className="flex items-center justify-between gap-4 rounded-lg border bg-white px-3 py-2.5">
    <span><span className="block text-sm">{label}</span>{hint && <span className="block text-[11px] text-zinc-400">{hint}</span>}</span>
    <Switch data-testid={testId} checked={!!checked} onCheckedChange={onChange} />
  </label>
);

export const ColorField = ({ label, value, onChange, testId }) => (
  <div className="flex items-center gap-2.5 rounded-lg border bg-white px-2.5 py-2">
    <label className="relative h-7 w-7 shrink-0 cursor-pointer overflow-hidden rounded-md border" style={{ background: value }}>
      <input type="color" data-testid={testId} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
    </label>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] text-zinc-500">{label}</div>
      <input value={value} data-testid={testId ? `${testId}-hex` : undefined} onChange={(e) => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && onChange(e.target.value.toUpperCase())}
        className="w-full bg-transparent font-mono-ui text-xs outline-none" />
    </div>
  </div>
);

export const IconUpload = ({ value, onChange, testId, title = "Icona personalizzata" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      onChange(await uploadImage(f));
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="relative inline-flex shrink-0">
      <button type="button" title={title} onClick={() => ref.current.click()} data-testid={testId}
        className="grid h-9 w-9 place-items-center overflow-hidden rounded-lg border border-dashed bg-white text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-700">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : value ? <img src={fileUrl(value)} alt="" className="h-6 w-6 object-contain" /> : <ImagePlus className="h-4 w-4" strokeWidth={1.5} />}
      </button>
      {value && !busy && (
        <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove`} title="Torna all'icona standard"
          className="absolute -right-1.5 -top-1.5 grid h-4 w-4 place-items-center rounded-full bg-zinc-900 text-white"><X className="h-2.5 w-2.5" /></button>
      )}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} />
    </span>
  );
};

export const ImageUpload = ({ label, value, onChange, testId, aspect = "square" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Immagine troppo grande (max 8 MB)");
    setBusy(true);
    try {
      onChange(await uploadImage(f));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  const box = aspect === "wide" ? "h-20 w-full" : "h-20 w-20";
  return (
    <div>
      <Label className="text-xs text-zinc-600">{label}</Label>
      <div className={`relative mt-1.5 ${aspect === "wide" ? "" : "w-fit"}`}>
        <button type="button" onClick={() => ref.current.click()} data-testid={testId} title={value ? "Sostituisci immagine" : "Carica immagine"}
          className={`${box} relative grid shrink-0 place-items-center overflow-hidden rounded-lg border border-dashed bg-white text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-700`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : value ? <img src={fileUrl(value)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5" strokeWidth={1.5} />}
        </button>
        {value && !busy && (
          <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove`} title="Rimuovi immagine" aria-label="Rimuovi immagine"
            className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-zinc-900 text-white shadow-md transition-colors hover:bg-red-600"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} />
    </div>
  );
};
